import { isArray } from 'lodash'
import { FormatMsg } from '~/common/localizationUtils'
import {
  DatasetField,
  FilterOperator,
  ReportDataTypes,
  ReportField,
  ReportQuery,
  ReportType,
  UnionMergeQuery
} from '~/custom_reports/types'

// maps form values to save report model
export const mapToSavedReport = ({
  query,
  id,
  name,
  description,
  owner,
  section,
  configExport,
  reportType,
  exportHyperlink,
  apiUsage,
  validateAccessibleQuery
}: {
  query?: ReportQuery | UnionMergeQuery
  id?: number
  name: string
  description: string
  owner: { id: string | number; type: string }
  section: string
  configExport: boolean
  reportType: ReportType
  exportHyperlink: boolean
  apiUsage: boolean
  validateAccessibleQuery?: boolean
}) => ({
  id,
  name,
  description,
  section_name: section,
  owner_id: owner.id,
  owner_type: owner.type,
  config_export_enabled: configExport,
  report_type: reportType,
  export_hyperlink_enabled: exportHyperlink,
  api_usage_enabled: apiUsage,
  validate_accessible_query: validateAccessibleQuery ?? true,
  ...(query && { query })
})

const checkMetadata = (field: DatasetField, metaField: string): boolean =>
  field && field.filter_metadata && field.filter_metadata[metaField]
const isBillingCycleDate = (field: ReportField) => checkMetadata(field as DatasetField, 'is_billing_cycle_date')
const isBillingCycle = (field: ReportField) => checkMetadata(field as DatasetField, 'is_billing_cycle')

interface FilterOperatorConfig {
  label: string
  value: FilterOperator
  data_types?: Array<ReportDataTypes>
  options?: any
  dynamic?: boolean
  predicate?: (field: DatasetField) => boolean
  multi?: boolean
}
const getBillingCycleFilterOperators = (msg: FormatMsg): Array<FilterOperatorConfig> => {
  const BILLING_CYCLE_FILTER_OPERATORS = msg
    ? [
        {
          label: msg('custom_reports.shared.equal_bc'),
          value: FilterOperator.EqualBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: msg('custom_reports.shared.not_equal_bc'),
          value: FilterOperator.NotEqualBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: msg('common.ui.greater_than'),
          value: FilterOperator.GreaterThanBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: msg('common.ui.greater_equal_to'),
          value: FilterOperator.GreaterThanOrEqualBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: msg('common.ui.less_than'),
          value: FilterOperator.LessThanBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: msg('common.ui.less_equal_to'),
          value: FilterOperator.LessThanOrEqualBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: msg('custom_reports.shared.previous_bc'),
          value: FilterOperator.PreviousBc,
          dynamic: true,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: msg('custom_reports.shared.current_bc'),
          value: FilterOperator.CurrentBc,
          dynamic: true,
          data_types: [ReportDataTypes.Integer]
        }
      ]
    : [
        {
          label: 'Equal BC',
          value: FilterOperator.EqualBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: 'Not Equal BC',
          value: FilterOperator.NotEqualBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: 'Greater Than',
          value: FilterOperator.GreaterThanBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: 'Greater / Equal To',
          value: FilterOperator.GreaterThanOrEqualBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: 'Less Than',
          value: FilterOperator.LessThanBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: 'Less / Equal To',
          value: FilterOperator.LessThanOrEqualBc,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: 'Previous BC',
          value: FilterOperator.PreviousBc,
          dynamic: true,
          data_types: [ReportDataTypes.Integer]
        },
        {
          label: 'Current BC',
          value: FilterOperator.CurrentBc,
          dynamic: true,
          data_types: [ReportDataTypes.Integer]
        }
      ]
  return BILLING_CYCLE_FILTER_OPERATORS
}

const getBaseFilterOperators = (msg: FormatMsg): Array<FilterOperatorConfig> => {
  const BASE_FILTER_OPERATORS = msg
    ? [
        {
          label: msg('custom_reports.shared.last_billing_cycle'),
          value: FilterOperator.LastBc,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime],
          predicate: isBillingCycleDate
        },
        {
          label: msg('common.ui.equal_to'),
          value: FilterOperator.Equal,
          data_types: [
            ReportDataTypes.String,
            ReportDataTypes.Date,
            ReportDataTypes.Datetime,
            ReportDataTypes.Number,
            ReportDataTypes.Integer,
            ReportDataTypes.Boolean
          ]
        },
        {
          label: msg('common.ui.contains'),
          value: FilterOperator.Contains,
          data_types: [ReportDataTypes.Text, ReportDataTypes.String]
        },
        {
          label: msg('common.ui.does_not_contain'),
          value: FilterOperator.DoesNotContain,
          data_types: [ReportDataTypes.Text, ReportDataTypes.String]
        },
        {
          label: msg('common.ui.not_equal_to'),
          value: FilterOperator.NotEqual,
          data_types: [
            ReportDataTypes.String,
            ReportDataTypes.Date,
            ReportDataTypes.Datetime,
            ReportDataTypes.Number,
            ReportDataTypes.Integer,
            ReportDataTypes.Boolean
          ]
        },
        {
          label: msg('common.ui.any_of'),
          value: FilterOperator.AnyOf,
          data_types: [ReportDataTypes.String, ReportDataTypes.Number, ReportDataTypes.Integer],
          multi: true
        },
        {
          label: msg('common.ui.not_any_of'),
          value: FilterOperator.NotAnyOf,
          data_types: [ReportDataTypes.String, ReportDataTypes.Number, ReportDataTypes.Integer],
          multi: true
        },
        {
          label: msg('common.ui.includes'),
          value: FilterOperator.Includes,
          data_types: [ReportDataTypes.Array],
          multi: true
        },
        {
          label: msg('common.ui.includes_any_of'),
          value: FilterOperator.IncludesAny,
          data_types: [ReportDataTypes.Array],
          multi: true
        },
        {
          label: msg('common.ui.does_not_include'),
          value: FilterOperator.NotIncludes,
          data_types: [ReportDataTypes.Array],
          multi: true
        },
        {
          label: msg('common.ui.includes'),
          value: FilterOperator.JsonIncludes,
          data_types: [ReportDataTypes.Json_Array],
          multi: false
        },
        {
          label: msg('common.ui.does_not_include'),
          value: FilterOperator.JsonNotIncludes,
          data_types: [ReportDataTypes.Json_Array],
          multi: false
        },
        {
          label: msg('common.ui.is_empty'),
          value: FilterOperator.JsonArrayIsEmpty,
          data_types: [ReportDataTypes.Json_Array],
          dynamic: true
        },
        {
          label: msg('common.ui.is_not_empty'),
          value: FilterOperator.JsonArrayNotEmpty,
          data_types: [ReportDataTypes.Json_Array],
          dynamic: true
        },
        {
          label: msg('custom_reports.shared.within_hierarchy'),
          value: FilterOperator.WithinHierarchy,
          predicate: (field: DatasetField) => Boolean(field && field.filter_type && field.filter_type === 'bu_picker'),
          multi: true
        },
        {
          label: msg('custom_reports.shared.not_within_hierarchy'),
          value: FilterOperator.NotWithinHierarchy,
          predicate: (field: DatasetField) => Boolean(field && field.filter_type && field.filter_type === 'bu_picker'),
          multi: true
        },
        { label: msg('common.ui.is_null'), value: FilterOperator.IsNull, dynamic: true },
        { label: msg('common.ui.is_not_null'), value: FilterOperator.NotNull, dynamic: true },
        {
          label: msg('common.ui.starts_with'),
          value: FilterOperator.StartsWith,
          predicate: (field: DatasetField) => {
            const data_types = [ReportDataTypes.String]
            if (!field) {
              return false
            }
            if (field.filter_type && ['options', 'bu_picker', 'jsf_select'].includes(field.filter_type)) {
              return false
            }
            return Boolean(field.type && data_types.indexOf(field.type) !== -1)
          }
        },
        {
          label: msg('common.ui.does_not_start_with'),
          value: FilterOperator.NotStartsWith,
          predicate: (field: DatasetField) => {
            const data_types = [ReportDataTypes.String]
            if (!field) {
              return false
            }
            if (field.filter_type && ['options', 'bu_picker', 'jsf_select'].includes(field.filter_type)) {
              return false
            }
            return Boolean(field.type && data_types.indexOf(field.type) !== -1)
          }
        },
        {
          label: msg('common.ui.greater_than'),
          value: FilterOperator.GreaterThan,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime, ReportDataTypes.Number, ReportDataTypes.Integer]
        },
        {
          label: msg('common.ui.greater_equal_to'),
          value: FilterOperator.GreaterThanOrEqual,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime, ReportDataTypes.Number, ReportDataTypes.Integer]
        },
        {
          label: msg('common.ui.less_than'),
          value: FilterOperator.LessThan,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime, ReportDataTypes.Number, ReportDataTypes.Integer]
        },
        {
          label: msg('common.ui.less_equal_to'),
          value: FilterOperator.LessThanOrEqual,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime, ReportDataTypes.Number, ReportDataTypes.Integer]
        },
        {
          label: msg('common.ui.today'),
          value: FilterOperator.Today,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.yesterday'),
          value: FilterOperator.Yesterday,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.tomorrow'),
          value: FilterOperator.Tomorrow,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.last_seven_days'),
          value: FilterOperator.Last7Days,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.next_seven_days'),
          value: FilterOperator.Next7Days,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.last_two_weeks'),
          value: FilterOperator.Last2Weeks,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.next_two_weeks'),
          value: FilterOperator.Next2Weeks,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.last_thirty_days'),
          value: FilterOperator.Last30Days,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.next_thirty_days'),
          value: FilterOperator.Next30Days,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.last_month'),
          value: FilterOperator.LastMonth,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.next_month'),
          value: FilterOperator.NextMonth,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.this_month'),
          value: FilterOperator.ThisMonth,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.last_six_months'),
          value: FilterOperator.Last6Months,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.next_six_months'),
          value: FilterOperator.Next6Months,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.last_twelve_months'),
          value: FilterOperator.Last12Months,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: msg('common.ui.next_twelve_months'),
          value: FilterOperator.Next12Months,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        }
      ]
    : [
        {
          label: 'Last Billing Cycle',
          value: FilterOperator.LastBc,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime],
          predicate: isBillingCycleDate
        },
        {
          label: 'Equal To',
          value: FilterOperator.Equal,
          data_types: [
            ReportDataTypes.String,
            ReportDataTypes.Date,
            ReportDataTypes.Datetime,
            ReportDataTypes.Number,
            ReportDataTypes.Integer,
            ReportDataTypes.Boolean
          ]
        },
        {
          label: 'Contains',
          value: FilterOperator.Contains,
          data_types: [ReportDataTypes.Text, ReportDataTypes.String]
        },
        {
          label: 'Does Not Contain',
          value: FilterOperator.DoesNotContain,
          data_types: [ReportDataTypes.Text, ReportDataTypes.String]
        },
        {
          label: 'Not Equal To',
          value: FilterOperator.NotEqual,
          data_types: [
            ReportDataTypes.String,
            ReportDataTypes.Date,
            ReportDataTypes.Datetime,
            ReportDataTypes.Number,
            ReportDataTypes.Integer,
            ReportDataTypes.Boolean
          ]
        },
        {
          label: 'Any Of',
          value: FilterOperator.AnyOf,
          data_types: [ReportDataTypes.String, ReportDataTypes.Number, ReportDataTypes.Integer],
          multi: true
        },
        {
          label: 'Not Any Of',
          value: FilterOperator.NotAnyOf,
          data_types: [ReportDataTypes.String, ReportDataTypes.Number, ReportDataTypes.Integer],
          multi: true
        },
        { label: 'Includes', value: FilterOperator.Includes, data_types: [ReportDataTypes.Array], multi: true },
        {
          label: 'Includes Any Of',
          value: FilterOperator.IncludesAny,
          data_types: [ReportDataTypes.Array],
          multi: true
        },
        {
          label: 'Does Not Include',
          value: FilterOperator.NotIncludes,
          data_types: [ReportDataTypes.Array],
          multi: true
        },
        {
          label: 'Includes',
          value: FilterOperator.JsonIncludes,
          data_types: [ReportDataTypes.Json_Array],
          multi: false
        },
        {
          label: 'Does Not Include',
          value: FilterOperator.JsonNotIncludes,
          data_types: [ReportDataTypes.Json_Array],
          multi: false
        },
        {
          label: 'Is Empty',
          value: FilterOperator.JsonArrayIsEmpty,
          data_types: [ReportDataTypes.Json_Array],
          dynamic: true
        },
        {
          label: 'Is Not Empty',
          value: FilterOperator.JsonArrayNotEmpty,
          data_types: [ReportDataTypes.Json_Array],
          dynamic: true
        },
        {
          label: 'Within Hierarchy',
          value: FilterOperator.WithinHierarchy,
          predicate: (field: DatasetField) => Boolean(field && field.filter_type && field.filter_type === 'bu_picker'),
          multi: true
        },
        {
          label: 'Not Within Hierarchy',
          value: FilterOperator.NotWithinHierarchy,
          predicate: (field: DatasetField) => Boolean(field && field.filter_type && field.filter_type === 'bu_picker'),
          multi: true
        },
        { label: 'Is Null', value: FilterOperator.IsNull, dynamic: true },
        { label: 'Is Not Null', value: FilterOperator.NotNull, dynamic: true },
        {
          label: 'Starts With',
          value: FilterOperator.StartsWith,
          predicate: (field: DatasetField) => {
            const data_types = [ReportDataTypes.String]
            if (!field) {
              return false
            }
            if (field.filter_type && ['options', 'bu_picker', 'jsf_select'].includes(field.filter_type)) {
              return false
            }
            return Boolean(field.type && data_types.indexOf(field.type) !== -1)
          }
        },
        {
          label: 'Does Not Start With',
          value: FilterOperator.NotStartsWith,
          predicate: (field: DatasetField) => {
            const data_types = [ReportDataTypes.String]
            if (!field) {
              return false
            }
            if (field.filter_type && ['options', 'bu_picker', 'jsf_select'].includes(field.filter_type)) {
              return false
            }
            return Boolean(field.type && data_types.indexOf(field.type) !== -1)
          }
        },
        {
          label: 'Greater Than',
          value: FilterOperator.GreaterThan,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime, ReportDataTypes.Number, ReportDataTypes.Integer]
        },
        {
          label: 'Greater / Equal To',
          value: FilterOperator.GreaterThanOrEqual,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime, ReportDataTypes.Number, ReportDataTypes.Integer]
        },
        {
          label: 'Less Than',
          value: FilterOperator.LessThan,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime, ReportDataTypes.Number, ReportDataTypes.Integer]
        },
        {
          label: 'Less / Equal To',
          value: FilterOperator.LessThanOrEqual,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime, ReportDataTypes.Number, ReportDataTypes.Integer]
        },
        {
          label: 'Today',
          value: FilterOperator.Today,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Yesterday',
          value: FilterOperator.Yesterday,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Tomorrow',
          value: FilterOperator.Tomorrow,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Last 7 days',
          value: FilterOperator.Last7Days,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Next 7 days',
          value: FilterOperator.Next7Days,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Last 2 weeks',
          value: FilterOperator.Last2Weeks,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Next 2 weeks',
          value: FilterOperator.Next2Weeks,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Last 30 days',
          value: FilterOperator.Last30Days,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Next 30 days',
          value: FilterOperator.Next30Days,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Last Month',
          value: FilterOperator.LastMonth,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Next Month',
          value: FilterOperator.NextMonth,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'This Month',
          value: FilterOperator.ThisMonth,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Last 6 Months',
          value: FilterOperator.Last6Months,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Next 6 Months',
          value: FilterOperator.Next6Months,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Last 12 Months',
          value: FilterOperator.Last12Months,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        },
        {
          label: 'Next 12 Months',
          value: FilterOperator.Next12Months,
          dynamic: true,
          data_types: [ReportDataTypes.Date, ReportDataTypes.Datetime]
        }
      ]
  return BASE_FILTER_OPERATORS
}

export const filterOperators = (msg: FormatMsg) => {
  return [...getBaseFilterOperators(msg), ...getBillingCycleFilterOperators(msg)]
}

const getOperator = (operator: FilterOperator, msg: FormatMsg): FilterOperatorConfig =>
  filterOperators(msg)
    .flatMap(f => (f.options ? f.options : f))
    .find((f: { value: FilterOperator }) => f.value === operator)

export const isDynamic = (operator: FilterOperator, msg: FormatMsg) => !!getOperator(operator, msg).dynamic

export const getFilterValueForOperator = (operator: FilterOperator, filter: { value: unknown }, msg: FormatMsg) => {
  if (isDynamic(operator, msg)) {
    return null
  }

  const isMultiValueFilter = getOperator(operator, msg).multi
  if (!isArray(filter.value) && isMultiValueFilter) {
    return []
  }

  if (isArray(filter.value) && !isMultiValueFilter) {
    return null
  }

  return filter.value
}

export const getFilterOperators = (
  type: ReportDataTypes,
  field: ReportField,
  aggregation: string | undefined,
  msg: FormatMsg
) => {
  if (isBillingCycle(field)) {
    return getBillingCycleFilterOperators(msg)
  }

  const areConcatenatedDates =
    [ReportDataTypes.Date, ReportDataTypes.Datetime].indexOf(type) !== -1 && aggregation === 'string_agg'
  const filterType = areConcatenatedDates ? ReportDataTypes.String : type
  return getBaseFilterOperators(msg).filter(operator =>
    operator.predicate
      ? operator.predicate({ ...field, type: filterType })
      : !operator.data_types || operator.data_types.indexOf(filterType) !== -1
  )
}
